import { BaseErrorResponse } from 'models/_base';
import { AirbnbGuestRequirements } from 'models/AirbnbGuestRequirements';
import { AirbnbListingHostDto } from 'models/AirbnbListingHost';
import { AreaUnitType } from 'models/AreaUnitType';
import {
  BookingDotComCheckInMethodHow,
  BookingDotComCheckInMethodType,
  BookingDotComCheckInMethodWhen,
} from 'models/bookingDotCom/CheckInMethods';
import { RoomTypes } from 'models/bookingDotCom/RoomTypes';
import { BookingType } from 'models/BookingType';
import { BookingWindow } from 'models/BookingWindow';
import { VrboCancellationPolicy, VrboPromotionPlan } from 'models/Channels';
import { CountryCode } from 'models/Country';
import { CurrencyWithoutNone } from 'models/Currency';
import { DayOfWeek } from 'models/DayOfWeek';
import { PropertyListingTypes } from 'models/PropertyListingTypes';
import { BedType, PropertyRoom } from 'models/PropertyRooms';
import { MultiUnitPropertyType } from 'models/PropertyType';

export interface PropertyDetails {
  propertyType: string;
  propertyName: string;
  weblink: string;
  floorCount: number;
  propertySize: number;
  propertySizeUnit: AreaUnitType;
  internalThumbnailUrl: string;
}

export interface PropertyAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  countryCode: CountryCode;
  latitude: number;
  longitude: number;
}

export interface CapacityDetails {
  bathroomCount: string;
  bedCount: number;
  bedroomCount: number;
  baseGuests: number;
  maxGuests: number;
  extraGuestFee: number;
  rooms?: (Omit<PropertyRoom, 'bedCounts'> & {
    beds: Partial<Record<BedType, number>>;
  })[];
}

export interface BookingSettings {
  bookingType: BookingType;
  bookingWindow: BookingWindow;
  turnoverDays: number;
  bookingLeadTime: number;
  daysOfTheWeekToCheckInOn: DayOfWeek[];
  checkInTime: number;
  checkoutTime: number;
  minimumStay: number;
  dayOfWeekMinimumStay: { [key in DayOfWeek]?: number };
  maximumStay: number;
  minimumWeekendStay: number;
  percentageUponReservation: number;
  fullPaymentTiming: number;
}

export interface MiscInfo {
  externalId: string;
  accountingId: string;
  rentalLicenseNumber: string;
  rentalLicenseExpirationDate: string;
  wifiNetwork: string;
  wifiPassword: string;
}

export enum AirbnbPromotionType {
  NEW_LISTING_PROMOTION = 'NEW_LISTING_PROMOTION',
}

export enum AirbnbPromotionStatus {
  AVAILABLE = 'AVAILABLE',
  ONGOING = 'ONGOING',
  EXPIRED = 'EXPIRED',
}

export interface AirbnbPromotion {
  type: AirbnbPromotionType;
  status: AirbnbPromotionStatus;
}

export interface AirbnbSettings {
  airbnbId: string;
  airbnbListingHosts: AirbnbListingHostDto[];
  listingType: PropertyListingTypes;
  bookingType: BookingType;
  allowBookingRequestAboveMaximumStay: boolean;
  allowBookingRequestBeyondBookingWindow: boolean | null;
  allowBookingRequestWithinBookingLeadTime: boolean;
  guestRequirements: AirbnbGuestRequirements;
  cancellationPolicy: string;
  // Read-only: returned by GET only, never sent in the PATCH body.
  eligibleCancellationPolicies: string[];
  checkInTimeStartFlexible: boolean | null;
  checkInTimeEnd: number | null;
  checkInTimeEndFlexible: boolean;
  checkInOption: string;
  checkInOptionInstruction?: string | null;
  nonRefundableRateEnabled: boolean;
  nonRefundableRateDiscount: number;
  sendMessageAsUserId: string | null;
  promotions?: AirbnbPromotion[];
  topRatedGuestDiscountEnabled: boolean | null;
  mobileOnlyDiscountEnabled: boolean | null;
}

export interface CheckInMethod {
  brandName?: string;
  how?: BookingDotComCheckInMethodHow;
  identifier?: string;
  text?: string;
  checkinMethod?: BookingDotComCheckInMethodType;
  when?: BookingDotComCheckInMethodWhen;
}

export interface BookingDotComSettings {
  bookingType: BookingType | null;
  hotelId: string | null;
  roomId: string | null;
  status: string | null;
  roomType: RoomTypes | null;
  roomName: string | null;
  primaryCheckInMethod: CheckInMethod | null;
  alternativeCheckInMethod: CheckInMethod | null;
  rateId: string | null;
  mobileDiscount: number | null;
  primaryContactUid: string | null;
  cancellationPolicyCode: number | null;
}

export interface VrboCustomCancellationPolicy {
  refundThreshold: number;
  refundPercentage: number;
}

export enum VrboPromotionStatus {
  SYNCED = 'SYNCED',
  NOT_SYNCED = 'NOT_SYNCED',
  ELIGIBLE = 'ELIGIBLE',
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  DELETED = 'DELETED',
  TO_BE_DELETED = 'TO_BE_DELETED',
}

export interface VrboPromotion {
  uid: string | undefined;
  type: VrboPromotionPlan;
  percentage: number | null;
  status?: VrboPromotionStatus;
}
export interface VrboSettings {
  agentUid: string | null;
  bookingType: BookingType;
  cancellationPolicy: VrboCancellationPolicy;
  customCancellationPolicies: VrboCustomCancellationPolicy[] | null;
  propertyId: string;
  showPropertyExactLocation: boolean;
  promotions: VrboPromotion[] | null;
}

export interface PropertyNotes {
  notes: string | null;
  notes_htmlContent?: string | null;
}

export interface PropertyMainSettingsData {
  propertyDetails: PropertyDetails;
  propertyAddress: PropertyAddress;
  capacityDetails: CapacityDetails;
  bookingSettings: BookingSettings;
  miscInfo: MiscInfo;
  airbnbSettings: AirbnbSettings;
  bookingDotComSettings: BookingDotComSettings;
  vrboSettings: VrboSettings;
  propertyUid: string;
  propertyNotes: PropertyNotes;
}

export interface HotelAddress extends PropertyAddress {}

export interface HotelBookingDotComSettings
  extends Pick<
    BookingDotComSettings,
    'bookingType' | 'primaryContactUid' | 'cancellationPolicyCode'
  > {
  alternative: BookingDotComSettings['alternativeCheckInMethod'];
  bookingDotComHotelId: BookingDotComSettings['hotelId'];
  primary: BookingDotComSettings['primaryCheckInMethod'];
}

export interface HotelDetails
  extends Pick<PropertyDetails, 'internalThumbnailUrl' | 'propertyName'> {
  currency: CurrencyWithoutNone;
  propertyType: MultiUnitPropertyType;
  securityDeposit?: number;
}

export interface HotelMiscInfo
  extends Pick<
    MiscInfo,
    'accountingId' | 'rentalLicenseNumber' | 'rentalLicenseExpirationDate'
  > {}

export interface HotelMainSettingsData {
  hotelAddress: HotelAddress;
  hotelBookingDotComSettings: HotelBookingDotComSettings;
  hotelDetails: HotelDetails;
  hotelMiscInfo: HotelMiscInfo;
  hotelUid: string;
}

export interface PropertyMainSettingsUpdateResponse extends BaseErrorResponse {}

export type HotelMainSettingsUpdatePayload = Omit<
  HotelMainSettingsData,
  'hotelBookingDotComSettings' | 'hotelUid'
> & {
  hotelBookingDotComSettings: Omit<
    HotelBookingDotComSettings,
    'alternative' | 'bookingDotComHotelId' | 'primary'
  > & {
    alternativeCheckInMethod: BookingDotComSettings['alternativeCheckInMethod'];
    primaryCheckInMethod: BookingDotComSettings['primaryCheckInMethod'];
  };
};
